import { create } from 'zustand';
import axios from 'axios';
import type { TokenDto } from '../api/generated';
import { decodeJwt } from '../utils/format';
import { useCartStore } from './cartStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

type BusinessType = 'ADVERTISER' | 'PLACEMENT_PROVIDER';

interface JwtPayload {
  sub?: string;
  userId?: string;
  roles?: string[];
  authorities?: Array<{ authority: string }>;
  exp?: number;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  userId: string | null;
  userUuid: string | null;
  isAdmin: boolean;
  businessEntityId: string | null;
  businessEntityType: BusinessType | null;
  businessEntityVerified: boolean | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (tokens: TokenDto) => Promise<void>;
  logout: () => void;
  setBusinessContext: (id: string, type: BusinessType) => void;
  updateTokens: (tokens: TokenDto) => void;
  rehydrate: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
}

// Monotonically incremented every time business context is set authoritatively (creating a
// business entity, logging in/out). A rehydrate() call that started before such a change can
// still be in flight when it happens (e.g. the once-per-app-load rehydrate() kicked off by
// AppRoutes on mount, running slowly on a bad connection while the user races through creating
// a business entity). Without this guard, that stale, already-in-flight call would resolve
// afterwards with pre-creation data and overwrite the fresh context with nulls - bouncing the
// user from /business/details straight back to /business/setup right after they just created
// the entity. Every business-context-affecting fetch captures the generation before starting
// and discards its result if the generation has since moved on.
let businessContextGeneration = 0;

const parsePayload = (token: string): JwtPayload | null =>
  decodeJwt(token) as JwtPayload | null;

const isAdminFromPayload = (payload: JwtPayload | null): boolean => {
  if (!payload) return false;
  const roles = payload.roles ?? payload.authorities?.map((a) => a.authority) ?? [];
  return roles.some((r) => r === 'ROLE_ADMIN' || r === 'ADMIN');
};

// Calls /users/info then /business-entities/{id} to get authoritative business context.
// Uses raw axios to avoid circular import with lib/api.ts.
const fetchBusinessContext = async (
  accessToken: string,
): Promise<{ userUuid: string; businessEntityId: string | null; businessEntityType: BusinessType | null; businessEntityVerified: boolean | null }> => {
  const headers = { Authorization: `Bearer ${accessToken}` };

  const userRes = await axios.get(`${API_BASE_URL}/api/v1/users/info`, { headers });
  const { id: userUuid, businessEntityId } = userRes.data as { id?: string; businessEntityId?: string };

  if (!businessEntityId) return { userUuid: userUuid ?? '', businessEntityId: null, businessEntityType: null, businessEntityVerified: null };

  const entityRes = await axios.get(
    `${API_BASE_URL}/api/v1/business-entities/${businessEntityId}`,
    { headers },
  );
  const entity = entityRes.data as { type?: string; verified?: boolean };
  const businessEntityType = (entity.type as BusinessType | undefined) ?? null;
  const businessEntityVerified = entity.verified ?? null;

  return { userUuid: userUuid ?? '', businessEntityId, businessEntityType, businessEntityVerified };
};

// Ensures accessToken in the store/localStorage is present and unexpired, refreshing it via
// the refresh token if needed. Returns the valid token, or null if the user isn't authenticated.
// Shared by rehydrate() and refreshAccessToken() - neither touches business-entity fields here.
const ensureValidAccessToken = async (
  set: (partial: Partial<AuthState>) => void,
): Promise<string | null> => {
  const storedAccess = localStorage.getItem('accessToken');
  const storedRefresh = localStorage.getItem('refreshToken');

  if (!storedAccess && !storedRefresh) return null;

  if (storedAccess) {
    const payload = parsePayload(storedAccess);
    const isExpired = payload?.exp != null && payload.exp * 1000 < Date.now();
    if (!isExpired) {
      const userId = payload?.sub ?? payload?.userId ?? null;
      set({
        accessToken: storedAccess,
        refreshToken: storedRefresh,
        userId,
        isAdmin: isAdminFromPayload(payload),
        isAuthenticated: true,
      });
      useCartStore.getState().switchUser(userId);
      return storedAccess;
    }
    localStorage.removeItem('accessToken');
  }

  if (!storedRefresh) return null;

  try {
    const res = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
      refreshToken: storedRefresh,
    });
    const tokens = res.data as { accessToken?: string; refreshToken?: string };
    if (!tokens.accessToken) throw new Error('No access token in refresh response');

    localStorage.setItem('accessToken', tokens.accessToken);
    if (tokens.refreshToken) localStorage.setItem('refreshToken', tokens.refreshToken);

    const payload = parsePayload(tokens.accessToken);
    const userId = payload?.sub ?? payload?.userId ?? null;
    set({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken ?? storedRefresh,
      userId,
      isAdmin: isAdminFromPayload(payload),
      isAuthenticated: true,
    });
    useCartStore.getState().switchUser(userId);
    return tokens.accessToken;
  } catch {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    return null;
  }
};

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  refreshToken: null,
  userId: null,
  userUuid: null,
  isAdmin: false,
  businessEntityId: null,
  businessEntityType: null,
  businessEntityVerified: null,
  isAuthenticated: false,
  isLoading: true,

  login: async (tokens) => {
    const { accessToken, refreshToken } = tokens;
    if (!accessToken) return;

    businessContextGeneration += 1;
    localStorage.setItem('accessToken', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

    const payload = parsePayload(accessToken);
    const userId = payload?.sub ?? payload?.userId ?? null;
    set({
      accessToken,
      refreshToken: refreshToken ?? null,
      userId,
      isAdmin: isAdminFromPayload(payload),
      businessEntityId: null,
      businessEntityType: null,
      businessEntityVerified: null,
      isAuthenticated: true,
    });

    // Merge any anonymous (guest) cart items into this user's cart
    useCartStore.getState().switchUser(userId, true);

    try {
      const ctx = await fetchBusinessContext(accessToken);
      if (ctx.userUuid) set({ userUuid: ctx.userUuid });
      if (ctx.businessEntityId) {
        localStorage.setItem('businessEntityId', ctx.businessEntityId);
        if (ctx.businessEntityType) {
          localStorage.setItem('businessEntityType', ctx.businessEntityType);
        }
        set({ businessEntityId: ctx.businessEntityId, businessEntityType: ctx.businessEntityType, businessEntityVerified: ctx.businessEntityVerified });
      }
    } catch {
      // Business context fetch failed - user will land on /business/setup if needed
    }
  },

  logout: () => {
    businessContextGeneration += 1;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('businessEntityId');
    localStorage.removeItem('businessEntityType');
    useCartStore.getState().switchUser(null);
    set({
      accessToken: null,
      refreshToken: null,
      userId: null,
      userUuid: null,
      isAdmin: false,
      businessEntityId: null,
      businessEntityType: null,
      businessEntityVerified: null,
      isAuthenticated: false,
    });
  },

  setBusinessContext: (id, type) => {
    businessContextGeneration += 1;
    localStorage.setItem('businessEntityId', id);
    localStorage.setItem('businessEntityType', type);
    // Newly created entities are never immediately verified
    set({ businessEntityId: id, businessEntityType: type, businessEntityVerified: false });
  },

  updateTokens: (tokens) => {
    const { accessToken, refreshToken } = tokens;
    if (!accessToken) return;
    localStorage.setItem('accessToken', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    set({ accessToken, refreshToken: refreshToken ?? null });
  },

  rehydrate: async () => {
    // Captured before any awaiting - if setBusinessContext()/login()/logout() runs while this
    // call is still in flight, its business-context result below is stale and gets discarded.
    const startGeneration = businessContextGeneration;
    const validToken = await ensureValidAccessToken(set);

    if (!validToken) {
      set({ isLoading: false });
      return;
    }

    // ── Fetch authoritative business context ─────────────────────────────
    try {
      const ctx = await fetchBusinessContext(validToken);
      if (businessContextGeneration === startGeneration) {
        if (ctx.userUuid) set({ userUuid: ctx.userUuid });
        if (ctx.businessEntityId) {
          localStorage.setItem('businessEntityId', ctx.businessEntityId);
          if (ctx.businessEntityType) {
            localStorage.setItem('businessEntityType', ctx.businessEntityType);
          }
          set({ businessEntityId: ctx.businessEntityId, businessEntityType: ctx.businessEntityType, businessEntityVerified: ctx.businessEntityVerified });
        } else {
          set({ businessEntityId: null, businessEntityType: null, businessEntityVerified: null });
        }
      }
    } catch {
      if (businessContextGeneration === startGeneration) {
        // API unreachable - fall back to what is in localStorage; verified status unknown until API is reachable
        const fallbackId = localStorage.getItem('businessEntityId');
        const fallbackType = localStorage.getItem('businessEntityType') as BusinessType | null;
        set({ businessEntityId: fallbackId, businessEntityType: fallbackType, businessEntityVerified: null });
      }
    }

    set({ isLoading: false });
  },

  // Refreshes the JWT only - never touches businessEntityId/Type/Verified. Use this (instead
  // of rehydrate()) right after optimistically setting business context via setBusinessContext,
  // e.g. just after creating a business entity: the freshly-created entity can briefly lag
  // behind in the backend's own "who is this user's business" lookup (read-replica delay), and
  // rehydrate()'s business-context fetch would otherwise wipe out the optimistic context we
  // just set and bounce the user back to /business/setup.
  refreshAccessToken: async () => {
    await ensureValidAccessToken(set);
  },
}));
